import { useNavigate } from '@tanstack/react-router'
import { Settings } from 'lucide-react'
import { type FC, useCallback } from 'react'

import { Button } from '@/components/button/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/dropdown-menu/dropdown-menu'

import { useDialog } from '@/lib/hooks/use-dialog'
import { type ResApplicantDetail } from '@/lib/queries/business/v1/types'

import { DeleteApplicantDialog } from '../applicant-dialogs/delete-applicant-dialog/delete-applicant-dialog'
import { EditApplicantDialog } from '../applicant-dialogs/edit-applicant-dialog/edit-applicant-dialog'

export const ApplicantSettings: FC<{
  applicant: ResApplicantDetail
  onInvalidate: () => Promise<void>
}> = ({ applicant, onInvalidate }) => {
  const navigate = useNavigate()
  const {
    isDialogOpen: isEditApplicantDialogOpen,
    openDialog: openEditApplicantDialog,
    closeDialog: closeEditApplicantDialog,
  } = useDialog()
  const { isDialogOpen: isDeleteDialogOpen, openDialog: openDeleteDialog, closeDialog: closeDeleteDialog } = useDialog()

  const handleEditClick = useCallback(() => {
    openEditApplicantDialog()
  }, [openEditApplicantDialog])

  const handleDeleteClick = useCallback(() => {
    openDeleteDialog()
  }, [openDeleteDialog])

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <Settings size={24} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={handleEditClick}>
              <span>Edit</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleDeleteClick}>
              <span>Delete</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditApplicantDialog
        open={isEditApplicantDialogOpen}
        pathId={String(applicant.path_id)}
        onEdited={onInvalidate}
        onClose={closeEditApplicantDialog}
      />

      <DeleteApplicantDialog
        applicant={applicant}
        open={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onDeleted={() =>
          navigate({
            to: '/applicants',
            search: {
              page: 1,
              pageSize: 10,
              sortKey: 'id',
              sortOrder: 'desc',
            },
          })
        }
      />
    </>
  )
}
